(function () {
  const SOURCE = 'gift-promotions';
  const PROMOTIONS_SECTION = 'promotions-data';
  const PROMOTIONS_TTL_MS = 5 * 60 * 1000;
  const VARIANT_AVAILABILITY_TTL_MS = 60 * 1000;
  const SYNC_DEBOUNCE_MS = 150;

  const TAG_CACHE = new Map();
  const VARIANT_AVAILABILITY_CACHE = new Map();

  let activeSync = null;
  let syncQueued = false;
  let syncDebounceTimer = null;
  let pendingCartData = null;

  let currentPromotions = Array.isArray(window.PROMOTIONS) ? window.PROMOTIONS : [];
  let promotionsLoadStatus = 'initial';
  let promotionsLoadedAt = 0;

  function parsePromotionsJson(html) {
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const jsonEl = doc.getElementById('promotions-data-json');
    if (jsonEl && jsonEl.textContent.trim()) {
      return JSON.parse(jsonEl.textContent);
    }

    const match = html.match(/id=["']promotions-data-json["'][^>]*>([\s\S]*?)<\/script>/i);
    if (match && match[1].trim()) {
      return JSON.parse(match[1]);
    }

    return null;
  }

  function normalizeConditionType(value) {
    return String(value || '').trim();
  }

  // Cart API (cart.js) は金額を最小通貨単位で返す（JPYでも 100円 → 10000）。
  // メタオブジェクトの threshold_amount は店舗通貨の表示単位（円）で入力する。
  const CART_MONEY_DIVISOR = 100;

  function cartAmountToStoreCurrency(amount) {
    return Number(amount) / CART_MONEY_DIVISOR;
  }

  function getLineTotal(item) {
    return cartAmountToStoreCurrency(item.final_line_price);
  }

  function normalizeVariantId(id) {
    if (id == null || id === '') return '';
    const value = String(id);
    if (value.includes('gid://')) {
      return value.split('/').pop();
    }
    return value;
  }

  function isSameVariantId(left, right) {
    return normalizeVariantId(left) === normalizeVariantId(right);
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) return tags.map((tag) => String(tag).trim()).filter(Boolean);
    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  }

  function syncTagCacheFromDom() {
    document.querySelectorAll('[data-product-handle][data-product-tags]').forEach((el) => {
      const handle = el.dataset.productHandle;
      if (!handle) return;
      TAG_CACHE.set(handle, normalizeTags(el.dataset.productTags));
    });
  }

  function hasTargetTag(item, targetTag) {
    if (!targetTag) return false;
    return item.product_tags.some((tag) => tag.toLowerCase() === String(targetTag).toLowerCase());
  }

  function isGiftVariant(item, promo) {
    return isSameVariantId(item.variant_id, promo.gift_variant_id);
  }

  function isGiftLine(item) {
    if (!item.properties) return false;
    return String(item.properties._gift) === 'true';
  }

  function isManagedGiftLine(item, promo) {
    return isGiftVariant(item, promo) && isGiftLine(item) && String(item.properties._promo_id) === String(promo.id);
  }

  function getPromoGiftLines(cart, promo) {
    return cart.items.filter((item) => {
      if (isGiftLine(item) && String(item.properties._promo_id) === String(promo.id)) {
        return true;
      }
      return isGiftVariant(item, promo);
    });
  }

  function isExcludedFromTarget(item, promotions) {
    if (isGiftLine(item)) return true;
    return promotions.some((promo) => isGiftVariant(item, promo));
  }

  async function loadPromotions(force = false) {
    if (
      !force &&
      promotionsLoadStatus === 'fresh' &&
      currentPromotions.length &&
      Date.now() - promotionsLoadedAt < PROMOTIONS_TTL_MS
    ) {
      return currentPromotions;
    }

    try {
      const response = await fetch(`/?sections=${PROMOTIONS_SECTION}&t=${Date.now()}`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Unexpected content-type: ${contentType || 'unknown'}`);
      }

      const data = await response.json();
      const html = data[PROMOTIONS_SECTION];
      if (!html) throw new Error('Section HTML missing');

      const promotions = parsePromotionsJson(html);
      if (!Array.isArray(promotions)) throw new Error('Promotion JSON invalid');

      currentPromotions = promotions;
      window.PROMOTIONS = promotions;
      promotionsLoadStatus = 'fresh';
      promotionsLoadedAt = Date.now();

      const countEl = document.getElementById('promotions-debug-active-count');
      if (countEl) countEl.textContent = String(promotions.length);

      const sourceEl = document.getElementById('promotions-debug-source');
      if (sourceEl) sourceEl.textContent = 'fresh';

      return promotions;
    } catch (error) {
      console.error('[gift-promotions] loadPromotions failed', error);
      promotionsLoadStatus = 'fallback';
      const sourceEl = document.getElementById('promotions-debug-source');
      if (sourceEl) sourceEl.textContent = `fallback (${error.message})`;
      return currentPromotions;
    }
  }

  function sectionsUrl() {
    if (window.location.pathname === window.routes.cart_url) {
      return window.routes.cart_url;
    }
    return window.location.pathname;
  }

  function updateDebugPanel(message, cart, evaluation) {
    const syncEl = document.getElementById('promotions-debug-sync');
    const cartEl = document.getElementById('promotions-debug-cart');
    const evalEl = document.getElementById('promotions-debug-eval');
    if (syncEl) syncEl.textContent = message;
    if (cartEl && cart) {
      cartEl.textContent = `cart items: ${cart.item_count}`;
    }
    if (evalEl && evaluation) {
      evalEl.textContent = evaluation;
    }
  }

  async function getProductTags(handle) {
    if (!handle) return [];
    if (TAG_CACHE.has(handle)) return TAG_CACHE.get(handle);

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return [];
      const product = await response.json();
      const tags = normalizeTags(product.tags);
      TAG_CACHE.set(handle, tags);
      return tags;
    } catch (error) {
      console.error('[gift-promotions] tag fetch failed', error);
      return [];
    }
  }

  async function getVariantAvailability(variantId) {
    const id = normalizeVariantId(variantId);
    const cached = VARIANT_AVAILABILITY_CACHE.get(id);
    if (cached && Date.now() - cached.at < VARIANT_AVAILABILITY_TTL_MS) {
      return cached.value;
    }

    try {
      const response = await fetch(`/variants/${id}.js`);
      if (!response.ok) return { available: false, quantity: 0 };

      const variant = await response.json();
      const tracked = variant.inventory_management === 'shopify';
      const available = !!variant.available;
      let quantity = 999;

      if (tracked && typeof variant.inventory_quantity === 'number') {
        quantity = Math.max(variant.inventory_quantity, 0);
      }

      const value = { available, quantity };
      VARIANT_AVAILABILITY_CACHE.set(id, { at: Date.now(), value });
      return value;
    } catch (error) {
      console.error('[gift-promotions] variant fetch failed', error);
      return { available: false, quantity: 0 };
    }
  }

  async function enrichCartItems(cart) {
    syncTagCacheFromDom();

    return Promise.all(
      cart.items.map(async (item) => {
        if (isGiftLine(item)) {
          return { ...item, product_tags: [] };
        }

        let product_tags = TAG_CACHE.get(item.handle);
        if (!product_tags) {
          product_tags = await getProductTags(item.handle);
        }

        return { ...item, product_tags };
      })
    );
  }

  function resolveCondition(promo) {
    const configuredType = normalizeConditionType(promo.condition_type);
    const thresholdAmount = Number(promo.threshold_amount);
    const thresholdQuantity = Number(promo.threshold_quantity) || 0;
    const hasAmount = Number.isFinite(thresholdAmount) && thresholdAmount > 0;
    const hasQuantity = thresholdQuantity > 0;
    let effectiveType = configuredType;
    let note = '';

    if (configuredType === 'target_per_amount' && !hasAmount && hasQuantity) {
      effectiveType = 'target_quantity';
      note = '金額未設定のため点数判定にフォールバック';
    } else if (configuredType === 'target_quantity' && !hasQuantity && hasAmount) {
      effectiveType = 'target_total';
      note = '点数未設定のため合計金額判定にフォールバック';
    } else if (configuredType === 'target_per_amount' && !hasAmount) {
      note = 'threshold_amount が未設定です';
    } else if (configuredType === 'target_quantity' && !hasQuantity) {
      note = 'threshold_quantity が未設定です';
    } else if (configuredType === 'target_total' && !hasAmount) {
      note = 'threshold_amount が未設定です';
    }

    return {
      configuredType,
      effectiveType,
      thresholdAmount: hasAmount ? thresholdAmount : 0,
      thresholdQuantity,
      note,
    };
  }

  function calculateGiftCount(promo, targetSubtotal, targetQty) {
    const { effectiveType, thresholdAmount, thresholdQuantity } = resolveCondition(promo);
    const giftQuantity = Number(promo.gift_quantity) || 1;
    const maxPerOrder = Number(promo.max_per_order) || 1;
    let count = 0;

    switch (effectiveType) {
      case 'target_total':
        count =
          thresholdAmount > 0 && targetSubtotal >= thresholdAmount ? giftQuantity : 0;
        break;
      case 'target_per_amount':
        count =
          thresholdAmount > 0
            ? Math.floor(targetSubtotal / thresholdAmount) * giftQuantity
            : 0;
        break;
      case 'target_quantity':
        count = thresholdQuantity > 0 && targetQty >= thresholdQuantity ? giftQuantity : 0;
        break;
      default:
        count = 0;
    }

    return Math.min(Math.max(count, 0), maxPerOrder);
  }

  function formatEvaluation(promo, snapshot) {
    const condition = resolveCondition(promo);
    const parts = [
      `${promo.name || promo.id}:`,
      `設定 ${condition.configuredType}`,
      condition.effectiveType !== condition.configuredType ? `判定 ${condition.effectiveType}` : null,
      `対象 ${snapshot.targetSubtotal}円 / ${snapshot.targetQty}点`,
    ];

    if (condition.effectiveType === 'target_quantity') {
      parts.push(`閾値 ${condition.thresholdQuantity}点`);
    } else {
      parts.push(`閾値 ${condition.thresholdAmount}円`);
    }

    parts.push(`特典 ${snapshot.desiredQty}個`, `cart ${snapshot.currentQty}個`);
    if (condition.note) parts.push(condition.note);

    return parts.filter(Boolean).join(' / ');
  }

  function collectSectionIds() {
    const sections = [];

    if (document.querySelector('cart-drawer')) {
      sections.push('cart-drawer', 'cart-icon-bubble');
    }

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push(mainCartItems.dataset.id);
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push(mainCartFooter.dataset.id);
    }

    return [...new Set(sections)];
  }

  function applySections(sections) {
    if (!sections) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && sections['cart-drawer']) {
      const target = document.querySelector('#CartDrawer');
      if (target) {
        target.innerHTML = cartDrawer.getSectionInnerHTML(sections['cart-drawer'], '#CartDrawer');
      }
      cartDrawer.classList.remove('is-empty');
      const inner = cartDrawer.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
    }

    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        bubble.innerHTML = new DOMParser()
          .parseFromString(sections['cart-icon-bubble'], 'text/html')
          .querySelector('.shopify-section').innerHTML;
      }
    }

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && sections[mainCartItems.dataset.id]) {
      const html = sections[mainCartItems.dataset.id];
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('.js-contents');
      const target = mainCartItems.querySelector('.js-contents');
      if (source && target) {
        target.innerHTML = source.innerHTML;
      }

      const cartItemsEl = document.querySelector('cart-items');
      const sourceCartItems = parsed.querySelector('cart-items');
      if (cartItemsEl && sourceCartItems) {
        cartItemsEl.classList.toggle('is-empty', sourceCartItems.classList.contains('is-empty'));
        cartItemsEl.className = sourceCartItems.className;
      }

      const cartForm = document.getElementById('cart');
      if (cartForm) {
        cartForm.classList.remove('critical-hidden');
      }
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && sections[mainCartFooter.dataset.id]) {
      const html = sections[mainCartFooter.dataset.id];
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('.js-contents');
      const target = mainCartFooter.querySelector('.js-contents');
      if (source && target) target.innerHTML = source.innerHTML;
    }

    syncTagCacheFromDom();
  }

  async function reloadCartSections() {
    const sections = collectSectionIds();
    if (!sections.length) return null;

    const response = await fetch(
      `${window.routes.cart_url}?sections=${sections.join(',')}&sections_url=${encodeURIComponent(sectionsUrl())}`
    );
    const data = await response.json();
    applySections(data);
    return data;
  }

  async function postCart(url, payload) {
    const sections = collectSectionIds();
    const body = {
      ...payload,
      sections,
      sections_url: sectionsUrl(),
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    return response.json();
  }

  function cartFromResponse(response) {
    if (response && Array.isArray(response.items)) {
      return response;
    }
    return null;
  }

  async function changeLine(item, quantity) {
    return postCart(window.routes.cart_change_url, {
      id: item.key,
      quantity,
    });
  }

  async function addGiftLine(promo, quantity) {
    return postCart(window.routes.cart_add_url, {
      items: [
        {
          id: normalizeVariantId(promo.gift_variant_id),
          quantity,
          properties: {
            _gift: 'true',
            _promo_id: promo.id,
          },
        },
      ],
    });
  }

  function evaluatePromotion(cart, enrichedItems, promo, promotions) {
    const targetItems = enrichedItems.filter(
      (item) => !isExcludedFromTarget(item, promotions) && hasTargetTag(item, promo.target_tag)
    );
    const targetSubtotal = targetItems.reduce((sum, item) => sum + getLineTotal(item), 0);
    const targetQty = targetItems.reduce((sum, item) => sum + item.quantity, 0);
    const desiredQty = calculateGiftCount(promo, targetSubtotal, targetQty);
    const giftLines = getPromoGiftLines(cart, promo);
    const currentQty = giftLines.reduce((sum, item) => sum + item.quantity, 0);

    return {
      targetSubtotal,
      targetQty,
      desiredQty,
      giftLines,
      currentQty,
      needsChange: desiredQty !== currentQty || (desiredQty === 0 && giftLines.length > 0),
    };
  }

  async function syncPromotion(cart, enrichedItems, promo, promotions) {
    const snapshot = evaluatePromotion(cart, enrichedItems, promo, promotions);
    let { targetSubtotal, targetQty, desiredQty, giftLines, currentQty } = snapshot;

    if (desiredQty > 0) {
      const availability = await getVariantAvailability(promo.gift_variant_id);
      if (!availability.available) {
        desiredQty = 0;
      } else {
        desiredQty = Math.min(desiredQty, availability.quantity);
      }
    }

    const managedLines = giftLines.filter((item) => isManagedGiftLine(item, promo));
    let response = null;
    let changed = false;
    let sections = null;

    const evaluation = formatEvaluation(promo, {
      targetSubtotal,
      targetQty,
      desiredQty,
      currentQty,
    });

    if (desiredQty === 0) {
      if (giftLines.length === 0) {
        return { changed: false, cart, response, evaluation, sections: null };
      }

      for (const line of giftLines) {
        response = await changeLine(line, 0);
        changed = true;
        if (response?.sections) sections = response.sections;
      }

      const updatedCart = cartFromResponse(response) || (changed ? await fetchCart() : cart);
      return { changed, cart: updatedCart, response, evaluation, sections };
    }

    if (promotionsLoadStatus !== 'fresh') {
      return { changed: false, cart, response, evaluation: `${evaluation} / 追加スキップ(ルール未取得)`, sections: null };
    }

    if (giftLines.length === 0) {
      response = await addGiftLine(promo, desiredQty);
      if (response?.sections) sections = response.sections;
      return {
        changed: true,
        cart: cartFromResponse(response) || (await fetchCart()),
        response,
        evaluation,
        sections,
      };
    }

    if (currentQty !== desiredQty) {
      response = await changeLine(managedLines[0] || giftLines[0], desiredQty);
      changed = true;
      if (response?.sections) sections = response.sections;
    }

    if (giftLines.length > 1) {
      cart = cartFromResponse(response) || (await fetchCart());
      const refreshedLines = getPromoGiftLines(cart, promo);
      for (let i = 1; i < refreshedLines.length; i += 1) {
        response = await changeLine(refreshedLines[i], 0);
        changed = true;
        if (response?.sections) sections = response.sections;
      }
    }

    return {
      changed,
      cart: cartFromResponse(response) || (changed ? await fetchCart() : cart),
      response,
      evaluation,
      sections,
    };
  }

  async function fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async function runSync(cartData) {
    updateDebugPanel('同期中...');

    const promotions = await loadPromotions();
    if (!promotions.length) {
      updateDebugPanel('ルールなし', null, '有効な promotion ルールがありません');
      return { changed: false, cart: null, sections: null };
    }

    let cart = cartData && Array.isArray(cartData.items) ? cartData : await fetchCart();
    let enrichedItems = await enrichCartItems(cart);

    let needsAnyChange = false;
    for (const promo of promotions) {
      if (evaluatePromotion(cart, enrichedItems, promo, promotions).needsChange) {
        needsAnyChange = true;
        break;
      }
    }

    if (!needsAnyChange) {
      const lastPromo = promotions[promotions.length - 1];
      const lastSnapshot = evaluatePromotion(cart, enrichedItems, lastPromo, promotions);
      const evaluation = formatEvaluation(lastPromo, lastSnapshot);
      updateDebugPanel('同期完了', cart, evaluation);
      return { changed: false, cart, sections: null };
    }

    let changed = false;
    let evaluationText = '';
    let latestSections = null;

    for (const promo of promotions) {
      const result = await syncPromotion(cart, enrichedItems, promo, promotions);
      if (result.evaluation) {
        evaluationText = result.evaluation;
      }
      if (result.sections) {
        latestSections = result.sections;
      }
      if (result.changed) {
        changed = true;
        cart = result.cart;
        enrichedItems = await enrichCartItems(cart);
      }
    }

    if (changed) {
      if (latestSections) {
        applySections(latestSections);
      } else {
        latestSections = await reloadCartSections();
      }
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
      }
    }

    updateDebugPanel(changed ? '同期完了（更新あり）' : '同期完了', cart, evaluationText);
    return { changed, cart, sections: latestSections };
  }

  function syncPromotions(cartData) {
    if (activeSync) {
      syncQueued = true;
      if (cartData) pendingCartData = cartData;
      return activeSync;
    }

    activeSync = runSync(cartData || pendingCartData)
      .catch((error) => {
        console.error('[gift-promotions]', error);
        updateDebugPanel('同期エラー');
        throw error;
      })
      .finally(() => {
        activeSync = null;
        pendingCartData = null;
        if (syncQueued) {
          syncQueued = false;
          syncPromotions();
        }
      });

    return activeSync;
  }

  function scheduleSync(cartData) {
    pendingCartData = cartData || pendingCartData;

    if (activeSync) {
      syncQueued = true;
      return activeSync;
    }

    clearTimeout(syncDebounceTimer);
    return new Promise((resolve, reject) => {
      syncDebounceTimer = setTimeout(() => {
        syncPromotions(pendingCartData).then(resolve).catch(reject);
      }, SYNC_DEBOUNCE_MS);
    });
  }

  window.syncGiftPromotions = syncPromotions;

  window.refreshGiftPromotionSections = async function refreshGiftPromotionSections(cartResponse) {
    const result = await syncPromotions(cartResponse);
    if (!cartResponse) return cartResponse;
    if (result?.sections) {
      return {
        ...cartResponse,
        sections: {
          ...(cartResponse.sections || {}),
          ...result.sections,
        },
      };
    }
    return cartResponse;
  };

  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === SOURCE || event.source === 'product-form') return undefined;
      return scheduleSync(event.cartData);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    syncPromotions().catch(() => {});
  });

  window.addEventListener('pageshow', (event) => {
    // Safari は通常リロード時にも pageshow 周りの挙動差が出やすいため、
    // bfcache 復帰時（persisted=true）のみ再同期する。
    if (!event || event.persisted !== true) return;
    syncPromotions().catch(() => {});
  });

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    let drawerSyncTimer = null;
    const observer = new MutationObserver(() => {
      if (!cartDrawer.classList.contains('active')) return;
      clearTimeout(drawerSyncTimer);
      drawerSyncTimer = setTimeout(() => {
        syncPromotions().catch(() => {});
      }, SYNC_DEBOUNCE_MS);
    });
    observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }
})();
